import { __ } from '@wordpress/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { ShareButton } from '../ShareButton';

const col = createColumnHelper();

const taxNames = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.name ).join( ', ' ) ?? '';

function taxFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.name === filterValue ) ?? false;
}
taxFilterFn.autoRemove = ( val ) => ! val;

const postTitles = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.title ).join( ', ' ) ?? '';

function postRefFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.title === filterValue ) ?? false;
}
postRefFilterFn.autoRemove = ( val ) => ! val;

const formatFileSize = ( bytes ) => {
	if ( ! bytes ) {
		return '';
	}
	const units = [ 'B', 'KB', 'MB', 'GB' ];
	let value = bytes;
	let unit = 0;
	while ( value >= 1024 && unit < units.length - 1 ) {
		value /= 1024;
		unit++;
	}
	return `${ value.toFixed( unit === 0 ? 0 : 1 ) } ${ units[ unit ] }`;
};

const ExternalLinkIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 20 20"
		fill="currentColor"
		className="inline-block w-4 h-4 shrink-0"
		aria-hidden="true"
	>
		<path d="M11 3a1 1 0 1 0 0 2h2.586l-6.293 6.293a1 1 0 1 0 1.414 1.414L15 6.414V9a1 1 0 1 0 2 0V4a1 1 0 0 0-1-1h-5Z" />
		<path d="M5 5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-3a1 1 0 1 0-2 0v3H5V7h3a1 1 0 1 0 0-2H5Z" />
	</svg>
);

const DownloadIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 20 20"
		fill="currentColor"
		className="inline-block w-4 h-4 shrink-0"
		aria-hidden="true"
	>
		<path d="M10 3a1 1 0 0 1 1 1v7.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L9 11.586V4a1 1 0 0 1 1-1Z" />
		<path d="M3 15a1 1 0 0 1 1 1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1a1 1 0 1 1 2 0v1a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-1a1 1 0 0 1 1-1Z" />
	</svg>
);

/**
 * Column definitions for the resource CPT.
 * @param root0
 * @param root0.onDelete
 */
export function resourceColumns( { onDelete } ) {
	return [
		col.display( {
			id: 'actions',
			header: '',
			cell: ( { row } ) => (
				<div className="flex flex-col gap-1 items-start">
					{ window.anacrm?.isStaff && (
						<button
							onClick={ () => {
								if (
									! window.confirm(
										__(
											'Move this resource to trash?',
											'anacrm'
										)
									)
								) {
									return;
								}
								onDelete( row.original.databaseId );
							} }
							className="px-2 py-1 text-xs border-2 border-red bg-white hover:bg-red hover:text-white cursor-pointer font-semibold whitespace-nowrap"
						>
							{ __( 'Delete', 'anacrm' ) }
						</button>
					) }
					<ShareButton uri={ row.original.uri } />
				</div>
			),
		} ),

		col.accessor( 'title', {
			header: __( 'Resource', 'anacrm' ),
			cell: ( { row, getValue } ) => (
				<a
					href={ row.original.uri }
					className="font-semibold text-blue hover:!no-underline"
				>
					{ getValue() }
				</a>
			),
		} ),

		col.accessor( 'contactWebsite', {
			id: 'contactWebsite',
			header: __( 'Link', 'anacrm' ),
			cell: ( { getValue } ) => {
				const url = getValue();
				return url ? (
					<a
						href={ url }
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1 text-blue hover:!no-underline"
						title={ url }
					>
						<ExternalLinkIcon />
						<span className="truncate max-w-[160px]">{ url }</span>
					</a>
				) : (
					'—'
				);
			},
		} ),

		col.accessor( ( row ) => row.file?.node ?? null, {
			id: 'file',
			header: __( 'File', 'anacrm' ),
			cell: ( { getValue } ) => {
				const file = getValue();
				if ( ! file?.mediaItemUrl ) {
					return '—';
				}
				return (
					<a
						href={ file.mediaItemUrl }
						download
						className="inline-flex items-center gap-1 text-blue hover:!no-underline"
						title={ file.title }
					>
						<DownloadIcon />
						<span>{ file.mimeType }</span>
						{ file.fileSize ? (
							<span className="text-gray">
								({ formatFileSize( file.fileSize ) })
							</span>
						) : null }
					</a>
				);
			},
		} ),

		col.accessor( ( row ) => taxNames( row.workScopes ), {
			id: 'workScopes',
			header: __( 'Work scope', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.resourceTypes ), {
			id: 'resourceTypes',
			header: __( 'Resource type', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.useTerms ), {
			id: 'useTerms',
			header: __( 'Use', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.durations ), {
			id: 'durations',
			header: __( 'Duration', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.keywords ), {
			id: 'keywords',
			header: __( 'Keywords', 'anacrm' ),
			filterFn: 'includesString',
		} ),

		col.accessor( ( row ) => taxNames( row.territories ), {
			id: 'territories',
			header: __( 'Territory', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => postTitles( row.orgRef ), {
			id: 'orgRef',
			header: __( 'Organisation', 'anacrm' ),
			filterFn: postRefFilterFn,
		} ),
	];
}
